// The Sales Order page's reads, list rules and quick edits: ONE copy, used by the desktop Sales
// Order page (pages/SalesOrderPage.jsx) and the phone order screens (mobile/screens/
// SalesOrdersScreen.jsx, SalesOrderDetailScreen.jsx). docs/mobile-pwa-plan.md "One copy of every
// action". Moved from SalesOrderPage word for word; SalesOrderPage.orderEdits.actions.test.jsx pins
// the list and the edits.
import config from '../../config.js';
import { isTmPending } from '../../utils/tmPricing';

const API_BASE = config.API_BASE_URL;
const noop = () => {};

// --- The order list ----------------------------------------------------------------------------

/** GET /sales_orders: Open orders, or every status with Show Completed/Shipped; archived ones only
 *  when asked. Resolves to the rows, or null when the API refuses (the list keeps what it had). */
export async function loadSalesOrders({ showClosed = false, showArchived = false } = {}) {
  const statusParam = showClosed ? '' : 'Open';
  const archParam = showArchived ? '&include_archived=true' : '';
  const res = await fetch(`${API_BASE}/sales_orders?status=${statusParam}${archParam}`);
  return res.ok ? res.json() : null;
}

/** A list row's risk chip, from lifecycle_stage (status is only Open/Cancelled) and the dates:
 *  complete | overdue | behind | on_track | no_date, or null for a cancelled order. Dates are read as
 *  local calendar days. */
export function listRiskLevel(order, now = new Date()) {
  const stage = order.lifecycle_stage;
  if (stage === 'Shipped' || stage === 'Completed' || stage === 'Invoiced') return 'complete';
  if (stage === 'Cancelled' || order.status === 'Cancelled') return null;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const toDate = (s) => (s ? new Date(String(s).slice(0, 10) + 'T00:00:00') : null);
  const promised = toDate(order.promised_date);
  const est = toDate(order.est_complete_date);
  if (!promised) return 'no_date';
  if (stage !== 'Draft' && promised < today) return 'overdue';
  if (est && est > promised) return 'behind';
  if (est) return 'on_track';
  return 'no_date';
}

/** The customers that have a listed order, by name (the customer filter's options). */
export function customersFromOrders(orders) {
  const byId = new Map();
  for (const o of orders || []) {
    if (o.customer_id == null) continue;
    const id = String(o.customer_id);
    if (!byId.has(id)) byId.set(id, o.customer_name || `Customer ID: ${o.customer_id}`);
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

const STAGE_ORDER = [
  'Draft', 'Released', 'In Production', 'Production Done', 'Needs More Jobs',
  'Awaiting Shipment', 'Partially Shipped', 'Shipped', 'Invoiced', 'Completed', 'Cancelled',
];

/** The lifecycle stages present in the list, in lifecycle order, then any others A-Z. */
export function stagesFromOrders(orders) {
  const set = new Set();
  for (const o of orders || []) if (o.lifecycle_stage) set.add(o.lifecycle_stage);
  const known = STAGE_ORDER.filter((s) => set.has(s));
  const extras = [...set].filter((s) => !STAGE_ORDER.includes(s)).sort();
  return [...known, ...extras];
}

/** The list after its filters. The search matches order #, customer PO and customer SO. */
export function filterOrders(orders, { searchTerm = '', statusFilter = [], customerFilter = '', tmPendingOnly = false } = {}) {
  const lower = searchTerm ? searchTerm.toLowerCase() : null;
  return (orders || []).filter((o) => {
    if (tmPendingOnly && !isTmPending(o)) return false;
    if (statusFilter.length > 0 && !statusFilter.includes(o.lifecycle_stage)) return false;
    if (customerFilter && String(o.customer_id) !== customerFilter) return false;
    if (lower) {
      const numMatch = o.order_number && o.order_number.toLowerCase().includes(lower);
      const poMatch = o.customer_po && o.customer_po.toLowerCase().includes(lower);
      const soMatch = o.customer_so && o.customer_so.toLowerCase().includes(lower);
      if (!numMatch && !poMatch && !soMatch) return false;
    }
    return true;
  });
}

// --- One order -----------------------------------------------------------------------------------

/** GET /sales_orders/{id}/truth plus every job's live progress (scoped to the order's customer),
 *  each tagged with where it came from (direct, or allocated from another order). Throws when the
 *  order itself can't be read. */
export async function loadOrderTruth(soId) {
  const truthRes = await fetch(`${API_BASE}/sales_orders/${soId}/truth`);
  if (!truthRes.ok) throw new Error('Failed to load order truth');
  const truth = await truthRes.json();
  const jobs = truth.related_documents?.jobs || [];
  const soCustomerId = truth.header?.customer_id;
  const custParam = soCustomerId ? `?customer_id=${soCustomerId}` : '';
  const jobProgressResults = await Promise.all(jobs.map((j) =>
    fetch(`${API_BASE}/jobs/${j.job_id}/progress${custParam}`).then((r) => (r.ok ? r.json() : null))));
  const jobSourceMap = {};
  jobs.forEach((j) => { jobSourceMap[j.job_id] = j; });
  return {
    ...truth,
    production_data: jobProgressResults.filter((j) => j !== null).map((j) => ({
      ...j,
      master_job_num: jobSourceMap[j.job_id]?.master_job_num ?? null,
      _source: jobSourceMap[j.job_id]?.source || 'direct',
      _source_so_id: jobSourceMap[j.job_id]?.source_so_id || null,
      _source_so_number: jobSourceMap[j.job_id]?.source_so_number || null,
    })),
  };
}

/** part_id → process_steps ("Laser,Coating") for parts no job carries yet, so a line with no job
 *  can still show its steps (lineSteps.routingSteps). One GET /parts/{id} per part; a part that
 *  can't be read is left out (its line shows no steps). */
export async function loadPartRoutings(partIds) {
  const ids = [...new Set((partIds || []).filter((id) => id != null))];
  const rows = await Promise.all(ids.map((id) =>
    fetch(`${API_BASE}/parts/${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)));
  const out = {};
  ids.forEach((id, i) => {
    if (rows[i] && rows[i].process_steps) out[id] = rows[i].process_steps;
  });
  return out;
}

/** The order's financial summary, pace and packing plan; each is null when there is none (most
 *  orders have no plan yet: 404 is normal). */
export async function loadOrderExtras(soId) {
  const [summaryRes, progressRes, planRes] = await Promise.all([
    fetch(`${API_BASE}/sales_orders/${soId}/production_summary`),
    fetch(`${API_BASE}/sales_orders/${soId}/production_progress`),
    fetch(`${API_BASE}/packaging/plans/by-so/${soId}`),
  ]);
  return {
    summary: summaryRes.ok ? await summaryRes.json() : null,
    progress: progressRes.ok ? await progressRes.json() : null,
    plan: planRes.ok ? await planRes.json() : null,
  };
}

/** GET /packaging/plans/by-so/{id}: the plan, or null when the order has none. Throws otherwise. */
export async function loadOrderPlan(soId) {
  const res = await fetch(`${API_BASE}/packaging/plans/by-so/${soId}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// --- Quick edits -------------------------------------------------------------------------------

export const QUICK_EDIT_FAILURE = Object.freeze({
  customerPo: 'Failed to update Customer PO.',
  customerSo: 'Failed to update Cust SO.',
  promised: 'Failed to update promised completion date.',
  target: 'Failed to update target completion date.',
});

/** Where the promised-completion edit starts: the order's date when it has one (lead time reset to
 *  Standard), else its lead time (Standard when none). */
export function promisedEditStart(header) {
  if (header?.promised_date) {
    return { mode: 'date', leadTime: 'Standard (10-14 Days)', date: header.promised_date.slice(0, 10) };
  }
  return { mode: 'lead_time', leadTime: header?.lead_time || 'Standard (10-14 Days)', date: '' };
}

/** The promised-completion PATCH: a lead time OR a specific date, the other one cleared. */
export function promisedPayload(mode, leadTime, date) {
  return {
    lead_time: mode === 'lead_time' ? leadTime : null,
    promised_date: mode === 'date' ? date : null,
  };
}

/** The API's schedule_propagation summary as a sentence (null when the save re-dated nothing). */
export function propagationNote(data) {
  const prop = data?.schedule_propagation;
  if (!prop) return null;
  const src = prop.source === 'target_completion_date' ? 'target date' : 'promised date';
  const d = new Date(prop.effective_date + 'T00:00:00').toLocaleDateString();
  let note = `Production re-dated to ${d} (${src}): ${prop.jobs_updated} job(s), ${prop.tasks_updated} task(s)`;
  if (prop.priority_applied) note += `, priority P${prop.priority_applied}`;
  if (prop.jobs_skipped?.length) {
    const nums = prop.jobs_skipped.map((sk) => `#${sk.master_job_num}`).join(', ');
    note += ` — kept manual dates on ${nums}`;
  }
  return note;
}

/** Save a few header fields (PATCH /sales_orders/{id}), then re-read the header. A refusal is
 *  told with ui.alert(failure) and resolves {ok:false}; onSaved(data) runs as soon as the PATCH is
 *  accepted (before the re-read). Resolves {ok:true, data, header}, header null when the re-read
 *  failed (keep the one shown). A network error on the PATCH throws. */
export async function saveOrderFields({ soId, patch, failure, ui, onSaved = noop }) {
  const res = await fetch(`${API_BASE}/sales_orders/${soId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    await ui.alert(failure);
    return { ok: false };
  }
  const data = await res.json();
  onSaved(data);
  // The save stands even when the re-read fails: header null = keep the one on screen (a missing
  // header used to blank the page).
  let header = null;
  try {
    const truthRes = await fetch(`${API_BASE}/sales_orders/${soId}/truth`);
    if (truthRes.ok) header = (await truthRes.json()).header || null;
  } catch (_) {
    header = null;
  }
  return { ok: true, data, header };
}

// --- Shipments ---------------------------------------------------------------------------------

/** /truth's shipment rows grouped into batches, newest first. Tasks belonging to a finalized SO
 *  plan collapse into ONE entry per plan PER BATCH — the batch being the pallet's physical
 *  out-the-door day (pallet_batch_at = staged_at for pickup, shipped_at for carrier). A plan that
 *  shipped in one go is a single row; split shipments (a reopened plan's second batch days later)
 *  get their own row and a batch-scoped manifest. Other shipments group by shipment_group_id
 *  (per-pallet ships before finalize, manual shipments, legacy data). A plan batch lists one line
 *  per product (quantities summed); other batches keep their per-task rows. */
export function shipmentBatches(shipments) {
  const groupedShipments = (shipments || []).reduce((acc, item) => {
    const isFinalized = !!item.plan_so_finalized_at && !!item.plan_id;
    const batchDay = isFinalized
      ? String(item.pallet_batch_at || item.plan_so_finalized_at || '').slice(0, 10)
      : null;
    const key = isFinalized
      ? `plan-${item.plan_id}-${batchDay}`
      : (item.shipment_group_id || 'Legacy');
    if (!acc[key]) {
      acc[key] = {
        id: key,
        planId: isFinalized ? item.plan_id : null,
        isPlanRollup: isFinalized,
        date: isFinalized
          ? (item.pallet_batch_at || item.plan_so_finalized_at)
          : item.shipped_date,
        groupIds: new Set(),
        items: [],
      };
    }
    if (isFinalized && item.shipment_group_id) {
      acc[key].groupIds.add(item.shipment_group_id);
    }
    // Legacy/per-group rows: use the latest task date for sort-by-date. Plan-batch rows keep the
    // pallet batch date (task shipped_date can lag/lead the physical out date and would smear
    // batches together).
    if (!acc[key].isPlanRollup) {
      const itemDate = item.shipped_date || acc[key].date;
      if (new Date(itemDate) > new Date(acc[key].date)) {
        acc[key].date = itemDate;
      }
    }
    acc[key].items.push(item);
    return acc;
  }, {});

  Object.values(groupedShipments).forEach((group) => {
    if (!group.isPlanRollup) return;
    const byProduct = group.items.reduce((acc, it) => {
      const k = it.product_id || '?';
      if (!acc[k]) acc[k] = { ...it, qty_completed: 0 };
      acc[k].qty_completed = (acc[k].qty_completed || 0) + (it.qty_completed || 0);
      return acc;
    }, {});
    group.items = Object.values(byProduct).sort(
      (a, b) => (a.product_id || '').localeCompare(b.product_id || ''),
    );
  });

  return Object.values(groupedShipments).sort((a, b) => new Date(b.date) - new Date(a.date));
}

/** A shipment batch's manifest path, or null for a legacy shipment without a batch ID.
 *  mode 'so' = the customer's per-order manifest (a plan batch scoped to its own shipment groups);
 *  'combined' = the driver's view of the whole physical shipment. */
export function batchManifestPath(batch, mode = 'so') {
  if (batch && batch.planId) {
    const groupsQs = (batch.groupIds && batch.groupIds.size)
      ? `?groups=${encodeURIComponent([...batch.groupIds].join(','))}`
      : '';
    return mode === 'combined'
      ? `/packaging/plans/${batch.planId}/combined-manifest.pdf`
      : `/packaging/plans/${batch.planId}/manifest.pdf${groupsQs}`;
  }
  if (!batch || !batch.id || batch.id === 'Legacy') return null;
  return `/reports/manifest/batch/${batch.id}`;
}

/** The order's own PDF, an invoice PDF. */
export const orderPdfPath = (soId) => `/sales_orders/${soId}/pdf`;
export const invoicePdfPath = (invoiceId) => `/invoices/${invoiceId}/pdf`;

// --- The Action Center ---------------------------------------------------------------------------

/** GET /sales_orders/action_items: {summary, categories}. Throws on a refusal. */
export async function loadActionItems() {
  const res = await fetch(`${API_BASE}/sales_orders/action_items`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/** The Action Center's sections, in triage order (the desktop renders each one its own way; the
 *  phone lists the orders under each title). */
export const AC_SECTION_META = Object.freeze({
  behind_schedule: Object.freeze({ key: 'behind_schedule', title: 'Behind Schedule', icon: 'bi-alarm', accent: '#dc3545', blurb: 'Promised dates missed or at risk' }),
  material_late: Object.freeze({ key: 'material_late', title: 'Material Arriving Late', icon: 'bi-truck', accent: '#e8590c', blurb: 'The PO that covers it lands too late' }),
  needs_more_jobs: Object.freeze({ key: 'needs_more_jobs', title: 'Needs More Jobs', icon: 'bi-exclamation-triangle-fill', accent: '#c02a37', blurb: "Released production won't cover the order" }),
  uncovered_remakes: Object.freeze({ key: 'uncovered_remakes', title: 'Remake Needed (Defected Parts)', icon: 'bi-arrow-repeat', accent: '#5a5adb', blurb: 'Defected assembly parts with no makeup sheet cut yet' }),
  material_blocked: Object.freeze({ key: 'material_blocked', title: 'Material / Component Shortage', icon: 'bi-box2-heart', accent: '#8b2635', blurb: "Open production a material can't cover" }),
  ready_to_seal: Object.freeze({ key: 'ready_to_seal', title: 'Ready to Seal', icon: 'bi-box2-fill', accent: '#fd7e14', blurb: 'Full boxes / pallets waiting on the operator' }),
  ready_to_ship: Object.freeze({ key: 'ready_to_ship', title: 'Ready to Ship / Stage', icon: 'bi-truck', accent: '#0d6efd', blurb: "Built but hasn't left the building" }),
  awaiting_pickup: Object.freeze({ key: 'awaiting_pickup', title: 'Awaiting Customer Pickup', icon: 'bi-person-walking', accent: '#ffc107', blurb: 'Staged pallets waiting on the customer' }),
  stalled: Object.freeze({ key: 'stalled', title: 'Stalled in Production', icon: 'bi-hourglass-split', accent: '#6f42c1', blurb: 'No shop-floor activity' }),
  uninvoiced: Object.freeze({ key: 'uninvoiced', title: 'Shipped, Not Invoiced', icon: 'bi-receipt', accent: '#198754', blurb: 'Billing action — money on the table' }),
  aging_drafts: Object.freeze({ key: 'aging_drafts', title: 'Aging Drafts', icon: 'bi-file-earmark-plus', accent: '#6c757d', blurb: 'Never released to production' }),
});

export const AC_SECTION_ORDER = Object.freeze(Object.keys(AC_SECTION_META));

/** Material Arriving Late, per order (jobq-api material_timing.py): the chip each verdict gets. An
 *  unknown verdict reads as at_risk (lateVerdict). */
export const AC_LATE_VERDICT = Object.freeze({
  late: {
    tone: 'danger', icon: 'bi-calendar-x', label: (it) => `Late ${it.days_late}d`,
    title: "Even with no wait at any station, the order can't finish by the promised date once this material lands.",
  },
  cut_before_material: {
    tone: 'dark', icon: 'bi-scissors', label: () => 'Cut before material',
    title: 'A cut is scheduled before its material lands (a pinned or stale date).',
  },
  at_risk: {
    tone: 'warning', icon: 'bi-hourglass-split', label: () => 'Tight',
    title: 'At best it finishes within 2 days of the promised date.',
  },
});

export const lateVerdict = (item) => AC_LATE_VERDICT[item?.verdict] || AC_LATE_VERDICT.at_risk;

const lateQty = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
};

/** One earlier cut in a Material Arriving Late line: "SO-1131 #10085", "stock job #10500",
 *  "SO-1150 (no job yet)". */
function lateCutLabel(c) {
  const so = c.order_number || null;
  if (c.master_job_num == null) return `${so || 'an order'} (no job yet)`;
  return so ? `${so} #${c.master_job_num}` : `stock job #${c.master_job_num}`;
}

/** The on-hand half of a Material Arriving Late line. `on_hand` is the order's VIEW of stock (stock
 *  less other orders' earmarks); `on_hand_left` is what reaches THIS order once the cuts queued ahead
 *  of it take theirs (jobq-api _material_late_by_so, 2026-10-16). Before it, "need 3 sheets · 4 on
 *  hand" read as covered when earlier cuts took 3 of the 4 (SO-1139). An API without on_hand_left
 *  reads as before. */
export function lateOnHandText(m, maxCuts = 3) {
  const onHand = lateQty(m?.on_hand);
  if (m?.on_hand_left == null) return `${onHand} on hand`;
  if (onHand <= 0) return 'none on hand';
  const left = lateQty(m.on_hand_left);
  const ahead = (m.taken_ahead || []).filter((c) => lateQty(c.units) > 0);
  const aheadUnits = lateQty(m.taken_ahead_units ?? ahead.reduce((a, c) => a + lateQty(c.units), 0));
  if (aheadUnits <= 0) return left >= onHand ? `${onHand} on hand` : `${left} of ${onHand} on hand left for it`;
  const shown = ahead.slice(0, maxCuts).map((c) => `${lateCutLabel(c)} ×${lateQty(c.units)}`);
  if (ahead.length > maxCuts) shown.push(`+${ahead.length - maxCuts} more`);
  const lead = left > 0 ? `${left} of ${onHand} on hand left for it` : `none of the ${onHand} on hand left for it`;
  return `${lead} (${aheadUnits} to earlier cuts: ${shown.join(', ')})`;
}

/** "2 from PO 1399 (Ryerson)" per PO the line waits on, joined with " + ". */
export function latePoText(pos) {
  return (pos || []).map((p) => `${p.qty != null ? `${lateQty(p.qty)} from ` : ''}PO ${p.po_number}`
    + (p.supplier ? ` (${p.supplier})` : '')).join(' + ');
}

// --- Lines -------------------------------------------------------------------------------------

const safeNum = (val) => {
  const n = parseFloat(val);
  return Number.isFinite(n) ? n : 0;
};

/** Defects on the order's OPEN jobs, by part id. Sheet / batch tasks carry part_id NULL but their
 *  defect rows each have a real part_id (defects_by_part), so they still attribute. Decoupled
 *  assembly defects (task_id NULL, from the assembly branch of POST /defects) count too: /truth's
 *  In Prod already subtracts them, so without them a line's started / defects / good breakdown
 *  can't explain the number on screen (SO-1102, 2026-08-05). Feed it to defectsByLine. */
export function activeDefectsByPart(productionData) {
  const map = {};
  (productionData || []).forEach((job) => {
    if (!['Pending', 'In Progress', 'Paused', 'Open'].includes(job.job_status)) return;
    (job.tasks || []).forEach((task) => {
      const perPart = Array.isArray(task.defects_by_part) ? task.defects_by_part : [];
      if (perPart.length > 0) {
        perPart.forEach((d) => {
          if (d?.part_id != null) map[d.part_id] = safeNum(map[d.part_id]) + safeNum(d.defective_quantity);
        });
      } else if (task.part_id) {
        map[task.part_id] = safeNum(map[task.part_id]) + safeNum(task.defect_quantity);
      }
    });
    (job.decoupled_defects || []).forEach((d) => {
      if (d?.part_id != null) map[d.part_id] = safeNum(map[d.part_id]) + safeNum(d.defective_quantity);
    });
  });
  return map;
}

/** Each part line's share of its part's open-job defects (activeDefectsByPart), keyed by
 *  sales_order_line_id. /truth (jobq-api 58afb13, Aug 2026) already splits a part's In Prod and
 *  Shipped across its lines (so_line_id order, the shares SUM to the part's figure), but the defects
 *  come per part, so they follow the line's In Prod share in whole pieces (largest remainder, a tie to
 *  the later line); the shares sum to the part's defects. A part with nothing in production puts them
 *  all on its first line. Lines group by part_id alone, as /truth's split does (a purchased line that
 *  carries a part_id is one of them). `flatLines` = flattened /truth lines. */
export function defectsByLine(flatLines, defectMap = {}) {
  const out = {};
  const groups = new Map();
  (flatLines || []).forEach((l) => {
    if (!l || l.part_id == null || l.sales_order_line_id == null) return;
    if (!groups.has(l.part_id)) groups.set(l.part_id, []);
    groups.get(l.part_id).push(l);
  });
  groups.forEach((grp, partId) => {
    grp.sort((a, b) => safeNum(a.sales_order_line_id) - safeNum(b.sales_order_line_id));
    const pool = safeNum(defectMap[partId]);
    const prod = grp.map((l) => Math.max(0, safeNum(l.qty_in_production)));
    const total = prod.reduce((s, p) => s + p, 0);
    if (total <= 0) {
      grp.forEach((l, i) => { out[l.sales_order_line_id] = i === 0 ? pool : 0; });
      return;
    }
    const exact = prod.map((p) => (pool * p) / total);
    const shares = exact.map(Math.floor);
    let left = pool - shares.reduce((s, n) => s + n, 0);
    exact
      .map((x, i) => ({ i, frac: x - shares[i] }))
      .sort((a, b) => b.frac - a.frac || b.i - a.i)
      .forEach(({ i }) => { if (left > 0) { shares[i] += 1; left -= 1; } });
    grp.forEach((l, i) => { out[l.sales_order_line_id] = shares[i]; });
  });
  return out;
}

/** The order's ordered / shipped totals and % shipped over every line (purchased ones too). /truth's
 *  per-line Shipped shares sum to the part's shipped, so a plain sum counts a part on several lines
 *  once. */
export function shippedTotals(flatLines) {
  const totalOrdered = (flatLines || []).reduce((acc, l) => acc + safeNum(l?.quantity_ordered), 0);
  const totalShipped = (flatLines || []).reduce((acc, l) => acc + safeNum(l?.qty_shipped), 0);
  const percentComplete = totalOrdered > 0 ? Math.round((totalShipped / totalOrdered) * 100) : 0;
  return { totalOrdered, totalShipped, percentComplete };
}
